import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { ChartBarIcon, ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { IStationOverTargetResult } from "../../../models/ProductionDashboard";
import DashboardTile from "../../dashboard/DashboardTile";
import LoadingWheel from "../LoadingWheel";
import { buildOverTargetChart, lineKey, OTHER_LINES_LABEL } from "./overTargetChart";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IStationOverTargetGraphProps {
  title: string;
  result?: IStationOverTargetResult;
  isLoading?: boolean;
  isError?: boolean;
  /** False when the picker has been cleared, so the empty state says "pick a range" not "nothing over". */
  hasRange?: boolean;
}

/** Minutes to one decimal, but "35 min" rather than "35.0 min". */
function formatMinutes(minutes: number, t: TFunction): string {
  return t("{{minutes}} min", { minutes: Number.isInteger(minutes) ? minutes : minutes.toFixed(1) });
}

/**
 * Minutes over the station's target, stacked by dismantling line, one bar per station.
 *
 * Stacked rather than grouped because the question is "where did the time go" before it is
 * "which line lost it": the bar height is the station's total, and the colours split it. The
 * legend doubles as the line filter so a single line can be read on its own.
 */
export default function StationOverTargetGraph({
  title,
  result,
  isLoading = false,
  isError = false,
  hasRange = true,
}: IStationOverTargetGraphProps) {
  const { t } = useTranslation("reports");
  const lines = useMemo(() => result?.lines ?? [], [result]);
  const lineKeys = useMemo(() => lines.map(lineKey), [lines]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [hideZero, setHideZero] = useState(false);

  // Default to every line, and re-select whenever the set of lines changes (a new range can
  // introduce a bay or the no-line bucket that the previous one did not have).
  useEffect(() => {
    setSelectedKeys(lineKeys);
  }, [lineKeys]);

  const chart = useMemo(
    () =>
      buildOverTargetChart(result, {
        hideZeroStations: hideZero,
        visibleKeys: selectedKeys.filter((key) => lineKeys.includes(key)),
      }),
    [result, hideZero, selectedKeys, lineKeys]
  );

  const data = useMemo(
    () => ({
      labels: chart.labels,
      datasets: chart.series.map((series) => ({
        // Line names come from the API; only the folded "Other lines" bucket is ours to translate.
        label: series.label === OTHER_LINES_LABEL ? t(OTHER_LINES_LABEL) : series.label,
        data: series.minutes,
        backgroundColor: series.colour,
        // A hairline of surface between stacked segments so adjacent colours never touch.
        borderColor: "#ffffff",
        borderWidth: { top: 1, bottom: 0, left: 0, right: 0 },
        maxBarThickness: 64,
        // Carried for the tooltip; Chart.js ignores unknown dataset keys.
        overCounts: series.overCounts,
        vehicleCounts: series.vehicleCounts,
      })),
    }),
    [chart, t]
  );

  const options = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: chart.series.length > 1, position: "top", labels: { boxWidth: 12 } },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: (item: TooltipItem<"bar">) => {
              const dataset = item.dataset as typeof data.datasets[number];
              const over = dataset.overCounts?.[item.dataIndex] ?? 0;
              const through = dataset.vehicleCounts?.[item.dataIndex] ?? 0;
              const minutes = typeof item.raw === "number" ? item.raw : 0;
              return t("{{label}}: {{minutes}} ({{over}} of {{through}} vehicles over)", {
                label: item.dataset.label,
                minutes: formatMinutes(minutes, t),
                over,
                through,
              });
            },
            footer: (items: TooltipItem<"bar">[]) => {
              const total = items.reduce((sum, item) => sum + (typeof item.raw === "number" ? item.raw : 0), 0);
              return items.length > 1 ? t("Total: {{minutes}}", { minutes: formatMinutes(Math.round(total * 10) / 10, t) }) : "";
            },
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: {
          stacked: true,
          beginAtZero: true,
          title: { display: true, text: t("Minutes over target") },
          grid: { display: false },
        },
      },
    }),
    [chart.series.length, data, t]
  );

  const selectionLabel =
    selectedKeys.length === 0
      ? t("No lines")
      : selectedKeys.length === lineKeys.length
      ? t("All lines")
      : t("{{count}} lines", { count: selectedKeys.length });

  return (
    <DashboardTile span={2}>
      <div className="p-3">
        <div className="mb-3 flex w-full items-start justify-between gap-4">
          <div className="prose min-w-0">
            <h3 className="mb-0 flex items-center">
              <ChartBarIcon className="inline-block w-6 h-6 mr-2 text-gray-500" />
              {title}
            </h3>
            <p className="mt-0.5 mb-0 text-sm text-gray-500">
              {t("Minutes each station ran past its target in the chosen range, split by dismantling line. Paused time is excluded and vehicles under target do not count against it.")}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {t("Hide zeroes")}
            </label>

            <Listbox value={selectedKeys} onChange={setSelectedKeys} multiple>
              <div className="relative w-56">
                <ListboxButton className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                  <span className="block truncate">{selectionLabel}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute right-0 z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {lines.length === 0 ? (
                      <div className="px-4 py-2 text-gray-400">{t("No lines")}</div>
                    ) : (
                      lines.map((line) => (
                        <ListboxOption
                          key={lineKey(line)}
                          value={lineKey(line)}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? "bg-primary-100 text-gray-900" : "text-gray-900"
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                                {line.name}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary-600">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      ))
                    )}
                  </ListboxOptions>
                </Transition>
              </div>
            </Listbox>
          </div>
        </div>

        {!hasRange ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">
            {t("Choose a date range to see time over target.")}
          </div>
        ) : isLoading ? (
          <div className="mt-6 min-h-[200px]">
            <LoadingWheel />
          </div>
        ) : isError && !result ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">
            {t("Could not load time over target. Try again in a moment.")}
          </div>
        ) : chart.stations.length === 0 ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">
            {hideZero && (result?.stations.length ?? 0) > 0
              ? t("No station ran over target in this range.")
              : t("No stations configured.")}
          </div>
        ) : chart.series.length === 0 ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">{t("No lines selected.")}</div>
        ) : (
          <>
            <div className="mt-6 h-96">
              <Bar options={options} data={data} />
            </div>
            {chart.isEmpty && (
              <p className="mt-3 text-center text-sm text-gray-500">
                {t("Nothing ran over target in this range.")}
              </p>
            )}
            {chart.untargeted.length > 0 && (
              <p className="mt-3 text-xs text-gray-500">
                {chart.untargeted.length === 1
                  ? t("No target is set for {{stations}}, so it cannot show time over. Targets are the average assembly time on each assembly.", { stations: chart.untargeted.join(", ") })
                  : t("No target is set for {{stations}}, so they cannot show time over. Targets are the average assembly time on each assembly.", { stations: chart.untargeted.join(", ") })}
              </p>
            )}
          </>
        )}
      </div>
    </DashboardTile>
  );
}
